import { getRetailerOrBrandToken } from "@faire/help-center-utils/serialized-data/getRetailerOrBrandToken";
import { markPageJsExecuted } from "@faire/web--source/common/performance/TrackPerformanceBreakdown/markPageJsExecuted";
import { useUserPerceivedLoadTimingWithKey } from "@faire/web--source/common/performance/userPerceivedLoadTiming/useUserPerceivedLoadTiming";
import { Column } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useQueryParams } from "@faire/web--source/ui/hooks/useQueryParams";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackCxFoundationsL1IssueClick } from "@faire/web-api--source/events/cxFoundations/click/l1Issue";
import { trackCxFoundationsTopicSelectPageView } from "@faire/web-api--source/events/cxFoundations/view/topicSelectPage";
import { QueryParameters as supportSelfServeParams } from "@faire/web-api--source/routes/support/self-serve";
import React, { useEffect } from "react";
import styled from "styled-components";

import { IssueTopics } from "../../components/IssueTopics";
import { useClearNodesInLocalStorageOnLoad } from "../../hooks/useClearNodesInLocalStorageOnLoad";
import { useNavigation } from "../../hooks/useNavigation";
import { useNodeSetter } from "../../hooks/useNodeSetter";
import { useSelectedNode } from "../../hooks/useSelectedNode";
import { useSkipNodeSelection } from "../../hooks/useSkipNodeSelection";
import { useTrackPageViewEffect } from "../../hooks/useTrackPageViewEffect";
import { useTree } from "../../hooks/useTree";
import { QueryState, SelfServeNode } from "../../types";

const PAGE_JS_EXECUTED_MARK = markPageJsExecuted();

export const Home: React.FC<QueryState> = ({ isLoading, dataUpdatedAt }) => {
  useTrackPageViewEffect(trackCxFoundationsTopicSelectPageView);
  useClearNodesInLocalStorageOnLoad();
  const { tree } = useTree();
  const { level1Node } = useSelectedNode();
  const { setLevel1Node } = useNodeSetter();
  const { goToNextStep } = useNavigation();
  const goToNextStepRef = React.useRef(goToNextStep);
  goToNextStepRef.current = goToNextStep;
  const retailerOrBrandToken = getRetailerOrBrandToken();

  const loadTimingRef = useUserPerceivedLoadTimingWithKey({
    name: "help_center-page_self_serve_home",
    enableTrackPerformanceBreakdown: "internal-tracking",
    isPageLoading: isLoading,
    pageLoadKey: "help_center-page_self_serve_home",
    pageJsExecutedMark: PAGE_JS_EXECUTED_MARK,
    dataUpdatedAt,
  });

  const { brand_order_token: brandOrderToken } = useQueryParams(
    supportSelfServeParams.parse
  );
  const { skipOrderSelection, nodeSelected, setNodeSelected } =
    useSkipNodeSelection();

  useEffect(() => {
    if (brandOrderToken) {
      setNodeSelected(true);
    }
  }, [brandOrderToken, setNodeSelected]);

  useEffect(() => {
    if (nodeSelected && level1Node) {
      goToNextStepRef.current({ skipOrderSelection });
      setNodeSelected(false);
    }
  }, [nodeSelected, level1Node, skipOrderSelection, setNodeSelected]);

  const handleNodeSelection = (node: SelfServeNode) => {
    setLevel1Node(node);
    setNodeSelected(true);
    trackCxFoundationsL1IssueClick(node.id, retailerOrBrandToken);
  };

  return (
    <Wrapper data-test-id="self-serve-home-wrapper">
      <IssueTopicsWrapper>
        <Typography variant="sectionHeaderSansMedium">
          <StrictLocalMsg
            defaultMessage="What can we help with?"
            description={{
              text: "Title for the page that provides a series of relevant issue topics for users to select",
            }}
          />
        </Typography>
        <Spacer height="3x" />
        <IssueTopics
          parentNode={tree["Root"]}
          selectedNode={level1Node}
          setSelectedNode={handleNodeSelection}
          loadTimingRef={loadTimingRef}
        />
      </IssueTopicsWrapper>
    </Wrapper>
  );
};

const Wrapper = styled(Column).attrs({
  align: { mobileAndAbove: "center", tabletAndAbove: "normal" },
  justify: "space-between",
})`
  ${responsiveMediaQueries.tabletAndAbove} {
    padding: ${getSpacing("4x", "5x")};
  }
  ${responsiveMediaQueries.mobile} {
    padding: ${getSpacing("4x", "2x")};
  }
`;

const IssueTopicsWrapper = styled(Column).attrs({
  align: "flex-start",
})`
  width: 100%;
`;
